import classNames from 'classnames';
import {Check, ChevronDown} from 'lucide-react';
import * as React from 'react';

export interface MenuItemProps {
    checked: boolean;
    onSelect: () => void;
    label: React.ReactNode;
    icon?: React.ReactNode;
    subLabel?: React.ReactNode;
}

export const MenuItem: React.FunctionComponent<MenuItemProps> = ({checked, onSelect, label, icon, subLabel}: MenuItemProps) => (
    <button type="button" role="menuitemradio" aria-checked={checked} className="mp-menu__item" onClick={onSelect}>
        {icon}
        <span className="mp-menu__txt">
            <span>{label}</span>
            {subLabel && <span className="mp-menu__sub">{subLabel}</span>}
        </span>
        <Check size={16} className="mp-menu__tick" aria-hidden="true"/>
    </button>
);

export interface MenuProps {
    triggerIcon: React.ReactNode;
    ariaLabel: string;
    heading: string;
    iconOnly?: boolean;
    align?: 'left' | 'right';
    defaultOpen?: boolean;
    children: React.ReactNode;
}

export const Menu: React.FunctionComponent<MenuProps> = ({triggerIcon, ariaLabel, heading, iconOnly, align = 'right', defaultOpen, children}: MenuProps) => {
    const [open, setOpen] = React.useState(!!defaultOpen);
    const rootRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const onPointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <div ref={rootRef} className="mp-menu" data-open={open}>
            <button
                type="button"
                className={classNames('mp-menu__trigger', {'mp-menu__trigger--icon': iconOnly})}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={() => setOpen(value => !value)}
            >
                {triggerIcon}
                <ChevronDown size={16} className="mp-menu__caret" aria-hidden="true"/>
            </button>
            <div
                className={classNames('mp-menu__panel', {'mp-menu__panel--left': align === 'left'})}
                role="menu"
                aria-label={heading}
                onClick={() => setOpen(false)}
            >
                <p className="mp-menu__heading">{heading}</p>
                {children}
            </div>
        </div>
    );
};
